import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, AlertTriangle, FilePlus, Loader2, Package, Users } from 'lucide-react'
import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { profileApi } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { EmissionChecklist } from './emission-checklist'

/**
 * /dashboard — Home post-auth.
 *
 * Lógica:
 *   1. Pregunta GET /facturador-profile/status.
 *   2. Si !has_profile o !onboarding_completed → redirige a /onboarding/wizard.
 *   3. Si todo OK → muestra resumen (resolución activa, próximas facturas G.6+).
 *
 * Por ahora solo placeholder con resolución activa + accesos rápidos
 * a los módulos que llegarán en G.5+ (Clientes, Productos, Facturar).
 */
export function DashboardPage() {
  const nav = useNavigate()
  const { activeOrgId, isLoading: authLoading } = useAuthContext()

  const status = useQuery({
    queryKey: ['profile-status', activeOrgId],
    queryFn: () => profileApi.status(activeOrgId!),
    enabled: !!activeOrgId,
  })

  // Si no completó onboarding → redirige al wizard.
  useEffect(() => {
    if (status.data && !status.data.onboarding_completed) {
      nav(ROUTES.WIZARD)
    }
  }, [status.data, nav])

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  if (status.isLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando estado...
        </div>
      </AppShell>
    )
  }

  if (status.isError) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Error cargando estado del facturador. Reintenta o contacta soporte.
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Bienvenido. Tu facturador está listo para emitir.
        </p>

        {/* Guía visual del camino completo a producción. */}
        <EmissionChecklist activeOrgId={activeOrgId} />

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ActiveCard
            to={ROUTES.INVOICES}
            icon={<FilePlus className="h-5 w-5 text-brand-600" />}
            title="Facturas"
            desc="Emisión + listado con cálculo IVA en vivo"
          />
          <ActiveCard
            to={ROUTES.CUSTOMERS}
            icon={<Users className="h-5 w-5 text-brand-600" />}
            title="Clientes"
            desc="Catálogo de adquirentes (PII cifrado)"
          />
          <ActiveCard
            to={ROUTES.PRODUCTS}
            icon={<Package className="h-5 w-5 text-brand-600" />}
            title="Productos"
            desc="Catálogo con IVA + INC por defecto"
          />
          <ActiveCard
            to={ROUTES.REPORTS}
            icon={<AlertTriangle className="h-5 w-5 text-brand-600" />}
            title="Reportes"
            desc="Ventas, IVA, INC por período"
          />
          <ActiveCard
            to={ROUTES.SETTINGS}
            icon={<CheckCircle2 className="h-5 w-5 text-brand-600" />}
            title="Configuración"
            desc="Profile, certificado, resolución DIAN"
          />
        </div>

        <div className="mt-8 rounded-md border border-yellow-300 bg-yellow-50 p-4 text-xs text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-none" />
            <div>
              <strong>Set de pruebas DIAN.</strong> Tu primera factura en producción
              requiere certificación previa (10 facturas + 1 NC + 1 ND aceptadas
              por DIAN en habilitación). Estado: pendiente.
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  )
}

function ActiveCard({
  to,
  icon,
  title,
  desc,
}: {
  to: string
  icon: React.ReactNode
  title: string
  desc: string
}) {
  return (
    <Link
      to={to}
      className="block rounded-xl border border-border bg-bg-surface p-5 transition-colors hover:border-brand-500 hover:bg-bg-elevated"
    >
      <div className="mb-2">{icon}</div>
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-xs text-fg-muted">{desc}</p>
    </Link>
  )
}
